import { FC, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';

import { useQuery } from 'react-query';
import qs from 'query-string';

import API from 'core/Api';
import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';
import { useLanguageContext } from 'store/language';
import { formatDate } from 'utils/helpers/formatters';
import { buildCalendarLegendItems } from 'utils/helpers/work-trips';

import { ISelectOptionProps } from 'components/form/types';
import Datepicker from 'components/form/Datepicker';
import MultiSelectFilter from 'components/table/MultiSelectFilter';

import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import bg from 'date-fns/locale/bg';
import styled from 'styled-components';

import CalendarEventWrapper from './CalendarEventWrapper';

import 'react-big-calendar/lib/css/react-big-calendar.css';

interface IWorkTripEvent {
  id: number;
  user_id: number;
  from_date: string;
  to_date: string;
  status: string;
  user_name: string;
  order_document_id: number | null;
  report_document_id: number | null;
  task?: string;
  destination?: string;
  displayed_destination?: string;
  route?: string;
  start: Date;
  end: Date;
}

interface ICountry {
  id: string;
  name: string;
}

interface IDropdowns {
  status?: ISelectOptionProps[];
  user?: ISelectOptionProps[];
  group?: ISelectOptionProps[];
  countries?: ICountry[];
}

interface IProps {
  Wrapper: FC<{ isLoading?: boolean; error?: unknown; children: React.ReactNode }>;
  entityName: string;
  showUserAndGroupFilters?: boolean;
}

const WorkTripsCalendarView: FC<IProps> = ({ Wrapper, entityName, showUserAndGroupFilters = true }: IProps) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();

  const messages = {
    allDay: t('leaves.employer.calendar.allDay'),
    previous: t('leaves.employer.calendar.previous'),
    next: t('leaves.employer.calendar.next'),
    today: t('leaves.employer.calendar.today'),
    month: t('leaves.employer.calendar.month'),
    week: t('leaves.employer.calendar.week'),
    day: t('leaves.employer.calendar.day'),
    agenda: t('leaves.employer.calendar.agenda'),
    date: t('leaves.employer.calendar.date'),
    time: t('leaves.employer.calendar.time'),
    event: t('leaves.employer.calendar.event'),
    showMore: (total: number) => t('leaves.employer.calendar.showMore', { total }),
  };

  const locales = {
    bg,
    en: enGB,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const [date, setDate] = useState<Date>(new Date());

  const calculateRangeDates = () => {
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const startDayPosition = startDate.getDay();
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const endDayPosition = endDate.getDay();

    startDate.setDate(startDate.getDate() - (startDayPosition - 1));
    endDate.setDate(endDate.getDate() + (7 - endDayPosition));

    return {
      startDate: formatDate(startDate, 'YYY-mm-dd'),
      endDate: formatDate(endDate, 'YYY-mm-dd'),
    };
  };

  const { startDate, endDate } = calculateRangeDates();

  const [filters, setFilters] = useState<any>({
    status: null,
    ...(showUserAndGroupFilters && { user: null, group: null }),
  });

  const { isLoading, data: calendarEventsData = [] } = useQuery<IWorkTripEvent[], Error>(
    [buildClientEntity(entityName, client), { startDate, endDate, ...filters }],
    (): Promise<IWorkTripEvent[]> => {
      const queryString = qs.stringify(filters, { arrayFormat: 'bracket', skipNull: true });
      const baseQuery = `limit=1000&offset=0&date_from=${startDate}&date_to=${endDate}`;
      return API.get(`work-trips/calendar?${queryString ? `${baseQuery}&${queryString}` : baseQuery}`).then(res =>
        res.data?.items?.map((trip: any) => ({
          ...trip,
          start: new Date(trip.from_date).setHours(0, 0, 0, 0),
          end: new Date(trip.to_date).setHours(23, 59, 59, 999),
        }))
      );
    },
    // Keeps the previously viewed month's events on screen while a new/adjacent
    // month is fetched, instead of unmounting the whole calendar behind a full loader
    // (isLoading only stays true for the very first, uncached load).
    { keepPreviousData: true }
  );

  const { isLoading: isLoadingDropdowns, data: dropdowns } = useQuery<IDropdowns, Error>(
    [buildClientEntity('work-trips-dropdowns', client)],
    (): Promise<IDropdowns> => API.get('work-trips/dropdown-options').then(res => res.data?.[language || 'bg']),
    { cacheTime: 0 }
  );

  const updateFilter = (key: string, selected: ISelectOptionProps[]) =>
    selected
      ? // @ts-ignore
        setFilters(fltrs => ({ ...fltrs, [key]: selected.map(opt => opt.value) }))
      : // @ts-ignore
        setFilters(fltrs => ({ ...fltrs, [key]: null }));

  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
      toolbar.onNavigate('prev');
      setDate(new Date(toolbar.date));
    };

    const goToNext = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
      toolbar.onNavigate('next');
      setDate(new Date(toolbar.date));
    };

    return (
      <StyledCustomToolbar>
        <button
          type="button"
          onClick={goToBack}
        >
          <FaChevronLeft size={16} />
        </button>

        <h5 className="text-center">
          {t(`leaves.months.${date.getMonth() + 1}`)} {date.getFullYear()}
        </h5>

        <button
          type="button"
          onClick={goToNext}
        >
          <FaChevronRight size={16} />
        </button>
      </StyledCustomToolbar>
    );
  };

  const allCalendarEvents = calendarEventsData.map(event => ({
    ...event,
    start: new Date(event.start),
    end: new Date(event.end),
  }));

  const legendItems = useMemo(() => buildCalendarLegendItems(dropdowns?.status), [dropdowns]);

  return (
    <Wrapper isLoading={isLoading || isLoadingDropdowns}>
      <CalendarControls className="mb-1 mb-lg-4">
        <CalendarLegend>
          {legendItems.map(item => (
            <LegendItem
              key={item.value}
              style={{ backgroundColor: item.backgroundColor, color: item.color }}
            >
              {item.label}
            </LegendItem>
          ))}
        </CalendarLegend>
        <FiltersRow className="d-flex flex-wrap justify-content-end">
          <Datepicker
            wrapperClassName="c-custom-select c-filter c-filter--small c-filter--date-width ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
            className="form-control"
            placeholder={t('leaves.employer.calendar.date')}
            onChange={(d: Date) => setDate(d || new Date())}
            selected={date}
            dateFormat="MM/YYY"
            showMonthYearPicker
          />
          <MultiSelectFilter
            className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
            // @ts-ignore
            value={dropdowns?.status?.filter(option => filters?.status?.includes(option.value))}
            placeholder={t('workTrips.status')}
            options={dropdowns?.status}
            onChange={selected => updateFilter('status', selected)}
            isClearable
            showTotal
            allowSelectAll
          />
          {showUserAndGroupFilters && (
            <>
              <MultiSelectFilter
                className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
                // @ts-ignore
                value={dropdowns?.user?.filter(option => filters?.user?.includes(option.value))}
                placeholder={t('workTrips.user')}
                options={dropdowns?.user}
                onChange={selected => updateFilter('user', selected)}
                isClearable
                showTotal
                limitOptions
                allowSelectAll
              />
              <MultiSelectFilter
                className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
                // @ts-ignore
                value={dropdowns?.group?.filter(option => filters?.group?.includes(option.value))}
                placeholder={t('workTrips.group')}
                options={dropdowns?.group}
                onChange={selected => updateFilter('group', selected)}
                isClearable
                showTotal
                allowSelectAll
              />
            </>
          )}
        </FiltersRow>
      </CalendarControls>
      <CustomExpandCalendar>
        <Calendar
          date={date}
          events={allCalendarEvents as any}
          localizer={localizer}
          culture={language || 'bg'}
          views={{ month: true }}
          components={{
            eventWrapper: CalendarEventWrapper,
            toolbar: CustomToolbar,
          }}
          toolbar
          messages={messages}
          popup
          style={{ height: 'auto' }}
        />
      </CustomExpandCalendar>
    </Wrapper>
  );
};

export default WorkTripsCalendarView;

const CustomExpandCalendar = styled.div`
  .rbc-month-row {
    display: inline-table !important;
    flex: 0 0 0 !important;
    min-height: 120px !important;
  }
`;

const CalendarControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FiltersRow = styled.div`
  .c-filter {
    max-width: 210px;
    width: 100%;
  }

  .c-filter--small {
    max-width: 80px;
  }

  @media (max-width: 992px) {
    .c-filter {
      max-width: 100%;
      width: 50%;
    }
  }

  @media (max-width: 768px) {
    .c-filter {
      width: 100%;
    }
  }
`;

const StyledCustomToolbar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  h5 {
    margin: 0;
    min-width: 360px;
  }

  button {
    border: 0;
    background: none;
    opacity: 0.4;
    transition: 0.3s;

    svg {
      fill: #575962;
    }

    &:hover {
      opacity: 1;
      transition: 0.3s;
    }
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  justify-content: flex-start;

  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const LegendItem = styled.div`
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
`;
